import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { ActualDepth } from '@skynet/data-liquidity-types';

import { BASE_DEPTH_LEVELS } from './types';
import type { DepthLevel, DepthViewOptionsState } from './types';

/** De-duplicate and sort depth levels ascending by numeric magnitude. */
function orderDepths(levels: readonly DepthLevel[]): DepthLevel[] {
  return [...new Set(levels)].sort((a, b) => Number(a) - Number(b));
}

/**
 * Absolute numeric magnitude of a raw depth key (e.g. `'-2'` → `2`, `'0.5'` → `0.5`,
 * `'12abc'` → `NaN`). `Number` (not `parseFloat`) is intentional: a key must be
 * wholly numeric to count, so partial-numeric junk yields `NaN` and is rejected
 * by callers.
 */
function parseDepthMagnitude(key: string | number): number {
  return Math.abs(Number(key));
}

/**
 * Map a raw depth key (e.g. `'0.5'`, `-2`, `'12'`) to its absolute magnitude as a
 * `DepthLevel`, or null when the key is not a finite non-zero number. No fixed
 * allow-list — any numeric depth the server sends is accepted.
 */
function toDepthLevel(key: string | number): DepthLevel | null {
  const magnitude = parseDepthMagnitude(key);
  return Number.isFinite(magnitude) && magnitude !== 0 ? String(magnitude) : null;
}

/**
 * Filter options to render for a domain: the always-present {@link BASE_DEPTH_LEVELS}
 * unioned with any depth magnitudes actually present in `maps` (e.g. the keys of
 * the server's liquidity payloads), ascending. So the base is always offered, and
 * server-sent depths beyond it (e.g. `±0.5%`, `±12%`) appear as extra toggles.
 * Non-numeric keys are ignored.
 */
export function getAvailableDepthLevels(
  maps: ReadonlyArray<Readonly<Record<string, unknown>> | undefined | null> = [],
): DepthLevel[] {
  const levels = new Set<DepthLevel>(BASE_DEPTH_LEVELS);

  for (const map of maps) {
    if (!map) {
      continue;
    }
    for (const key of Object.keys(map)) {
      const level = toDepthLevel(key);
      if (level) {
        levels.add(level);
      }
    }
  }

  return orderDepths([...levels]);
}

/**
 * Whether the view is narrowed — i.e. some available depth level is not enabled.
 * Drives the "active filters" badge dot on the depth view-options control.
 */
export function hasHiddenDepthLevels(
  available: readonly DepthLevel[],
  enabled: readonly DepthLevel[],
): boolean {
  return available.some((level) => !enabled.includes(level));
}

/**
 * Expand enabled magnitudes into the symmetric ± `ActualDepth` list consumed by
 * the app liquidity selectors (e.g. `['1','2']` → `['1','2','-1','-2']`). The
 * order mirrors the previously hardcoded arrays: positives ascending, then their
 * negatives. Ordering is irrelevant to correctness — depths are consumed as a
 * keyed map — but kept stable for readability and snapshot stability.
 */
export function toActualDepths(levels: readonly DepthLevel[]): ActualDepth[] {
  const ordered = orderDepths(levels);
  const negatives = ordered.map((level): ActualDepth => `-${level}`);
  return [...ordered, ...negatives];
}

/**
 * Numeric absolute magnitudes for the enabled levels (e.g. `['1','2']` → `[1, 2]`),
 * ascending. Passed to `SkyDepthItem` to bound which depth rows it renders.
 */
export function toDepthNumbers(levels: readonly DepthLevel[]): number[] {
  return orderDepths(levels).map(Number);
}

/**
 * Keep only the entries of a depth-keyed map (e.g. a `FormattedLiquidityMap`)
 * whose absolute magnitude is one of `levels`. Used to narrow task liquidity
 * blocks to the enabled depths. Both ask (`'1'`) and bid (`'-1'`) sides of an
 * enabled magnitude are kept. Returns a new object; the input is not mutated.
 */
export function filterDepthMap<T>(
  map: Partial<Record<ActualDepth, T>>,
  levels: readonly number[],
): Partial<Record<ActualDepth, T>> {
  const allowed = new Set(levels);
  return Object.fromEntries(
    Object.entries(map).filter(([key]) => allowed.has(parseDepthMagnitude(key))),
  );
}

/** Build a full depth view-options state, applying optional seed overrides. */
export function initDepthViewOptionsState(
  opts?: Partial<DepthViewOptionsState>,
): DepthViewOptionsState {
  const initialState: DepthViewOptionsState = {
    enabledDepths: [...BASE_DEPTH_LEVELS],
  };

  return {
    ...initialState,
    ...opts,
    enabledDepths: orderDepths(opts?.enabledDepths ?? initialState.enabledDepths),
  };
}

/** Props for the depth view-options slice factory. */
export interface InitDepthViewOptionsSliceProps {
  /** Redux slice name (e.g. `loansDepthViewOptions`). */
  sliceName?: string;
  /** Optional pre-seeded state (per-domain defaults). */
  initialState?: DepthViewOptionsState;
}

/**
 * Creates a depth view-options Redux slice (reducer + actions) for a single
 * domain. State holds the enabled depth magnitudes; the app persists the slice
 * and feeds the enabled levels into the liquidity selectors and `SkyDepthItem`.
 */
export function initDepthViewOptionsSlice({
  sliceName = 'depthViewOptions',
  initialState = initDepthViewOptionsState(),
}: InitDepthViewOptionsSliceProps = {}) {
  const slice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      /** Toggle a single depth level on/off. */
      toggleDepth(state, action: PayloadAction<DepthLevel>) {
        const level = action.payload;
        state.enabledDepths = state.enabledDepths.includes(level)
          ? state.enabledDepths.filter((depth) => depth !== level)
          : orderDepths([...state.enabledDepths, level]);
      },
      /** Replace the enabled set wholesale. */
      setEnabledDepths(state, action: PayloadAction<readonly DepthLevel[]>) {
        state.enabledDepths = orderDepths(action.payload);
      },
      /** Restore the per-domain default enabled set. */
      resetEnabledDepths(state) {
        state.enabledDepths = [...initialState.enabledDepths];
      },
    },
  });

  return {
    actions: slice.actions,
    reducer: slice.reducer,
  };
}
